/**
 * Mapping AnalyseResult (API externe Insighball Video Analysis)
 * vers structures d'affichage (rapport match).
 *
 * Equipe 1 / Equipe 2 = equipes detectees dans la video.
 * Convention UI : equipe_1 = "Nous" (ou club), equipe_2 = adversaire.
 *
 * CONVENTION D'AFFICHAGE — un zero et une absence ne sont pas la meme chose.
 * Une valeur mesuree a zero s'affiche 0. Une valeur que le prestataire n'a pas
 * produite s'affiche en tiret. Ce fichier est l'endroit ou la distinction se
 * perdait : tout passait par num(), qui rend 0 sur une valeur absente, et le
 * rapport annoncait donc « 0 tir » a un coach dont les tirs n'avaient jamais
 * ete comptes. Les valeurs venant du payload passent maintenant par
 * numOrNull() ; StatRow rend le tiret sur tout ce qui n'est pas un nombre.
 */

/**
 * Coercition tolerante : une valeur illisible vaut zero.
 *
 * A RESERVER aux valeurs deja garanties presentes, ou aux fragments d'une
 * chaine qu'on vient de decouper soi-meme (voir parseRatio). Pour une valeur
 * lue directement dans le payload du prestataire, utiliser numOrNull().
 */
/**
 * Couleurs sur lesquelles l'analyse a été faite — COULEURS-MAILLOT-001.
 *
 * Rend `{ nous, adversaire }` quand le prestataire a renvoyé le bloc `equipes`,
 * `null` sinon.
 *
 * CE `null` EST LA PARTIE IMPORTANTE. Tout l'historique — 68 matchs au 10/08 —
 * a été analysé avant que les couleurs existent et n'a donc pas de bloc
 * `equipes`. Il ne faut RIEN en déduire : ces analyses gardent exactement le
 * comportement d'affichage qu'elles avaient, et le rapport n'affirme rien sur
 * l'identité des équipes.
 *
 * Le repli « equipe_1 = nous par défaut » serait le pire choix possible · c'est
 * précisément lui qui produit un rapport inversé sans le dire. Quand on ne sait
 * pas, on se tait.
 *
 * Le bloc n'est lu QUE pour être affiché. Il ne réordonne rien : quand il est
 * présent, `equipe_1` est déjà notre équipe par construction, puisque c'est
 * nous qui avons envoyé `team_1_color`.
 */
export function analyseResultToEquipes(result) {
  const eq = result?.equipes
  if (!eq || typeof eq !== 'object') return null
  const nous = eq.equipe_1?.couleur
  const adversaire = eq.equipe_2?.couleur
  // Les deux ou rien · une seule couleur n'ancre aucune équipe, et l'afficher
  // seule laisserait croire à une identification qui n'a pas eu lieu.
  if (typeof nous !== 'string' || typeof adversaire !== 'string') return null
  if (!nous || !adversaire) return null
  return { nous, adversaire }
}

function num(v, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Coercition stricte : une valeur absente ou illisible vaut null, pas zero.
 *
 * C'est ce null qui remonte jusqu'a StatRow et devient un tiret. Un vrai zero
 * envoye par le prestataire traverse intact et s'affiche 0.
 */
function numOrNull(v) {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Longueur d'une liste du payload, ou null si la liste est absente.
 *
 * Une liste vide est une MESURE : le prestataire a cherche, il n'a rien
 * trouve, le compte est zero. Une clef absente n'est pas une mesure.
 *
 * Depuis la PR backend #109, l'agregat des deux periodes respecte cette
 * distinction : `tirs_equipe_1: []` quand les deux periodes ont mesure sans
 * rien trouver, clef absente quand aucune des deux n'a produit de bloc
 * `cartographies`. Les analyses stockees AVANT ce deploiement portent null
 * dans les deux cas ; elles s'affichent donc en tiret, ce qui reste honnete :
 * on ne peut plus savoir laquelle des deux situations elles decrivent.
 */
function listLengthOrNull(v) {
  return Array.isArray(v) ? v.length : null
}

/**
 * Parse "reussies/totales" → { ok, total, pct }. Chaque membre vaut un nombre
 * ou null ; `0/0` est une mesure et traverse en ok = 0, total = 0.
 *
 * `ok` et `total` étaient auparavant calculés puis jetés : seul `pct` était
 * remonté. Ils sont maintenant exposés (le terrain affiche le volume), ce qui
 * a rendu visibles deux zéros fabriqués que le contrat précédent produisait ·
 *
 *   - chaîne absente ou sans '/' → rendait { ok: 0, total: 0 }, soit « aucune
 *     passe tentée » là où la mesure n'existe pas. Rend null/null.
 *   - fragment gauche illisible ("abc/60") → num() le rendait 0, donc
 *     pct = round(0/60) = 0. Le rapport affichait « 0 % » de précision sur une
 *     zone dont les passes réussies n'ont jamais été lues. C'était le dernier
 *     endroit du fichier où num() s'appliquait encore à du payload ; le
 *     commentaire qui l'y défendait ne raisonnait que sur le fragment DROIT.
 *
 * Un `pct` n'est donc rendu que si les deux fragments sont des nombres et que
 * le total est strictement positif — même règle que passAccuracy.
 */
export function parseRatio(str) {
  if (typeof str !== 'string' || !str.includes('/')) {
    return { ok: null, total: null, pct: null }
  }
  const [a, b] = str.split('/')
  const ok = numOrNull(a)
  const total = numOrNull(b)
  if (ok === null || total === null || total <= 0) {
    return { ok, total, pct: null }
  }
  return { ok, total, pct: Math.round((ok / total) * 100) }
}

/** Paire de valeurs du payload. Absente d'un cote → tiret de ce cote seul. */
function pctPair(a, b) {
  return [numOrNull(a), numOrNull(b)]
}

/**
 * Precision de passe, en pourcentage, ou null si elle n'est pas calculable.
 *
 * Trois cas rendent null : total absent, reussies absent, total a zero. Le
 * dernier n'est pas une division par zero deguisee — une equipe qui n'a tente
 * aucune passe n'a pas une precision de 0 %, elle n'en a pas.
 */
function passAccuracy(reussies, total) {
  const r = numOrNull(reussies)
  const t = numOrNull(total)
  if (r === null || t === null || t <= 0) return null
  return Math.round((r / t) * 100)
}

/**
 * Marqueur d'une ligne que le match complet ne peut pas produire.
 *
 * Trois blocs sont des pourcentages que le prestataire ne fournit pas sous
 * forme de compteurs bruts : possession globale, possession par zone, origine
 * des attaques. Les moyenner ne serait exact que si les deux périodes avaient
 * la même durée de jeu effectif. Le backend les laisse donc absents de
 * l'agrégat (P0-STATS-001), et l'affichage le dit au lieu de montrer un zéro
 * qui passerait pour une mesure.
 */
export const NOT_AGGREGATABLE = Symbol('not-aggregatable')

/**
 * Comme pctPair, mais rend NOT_AGGREGATABLE quand la donnée est absente ET
 * qu'on lit un agrégat de match complet.
 *
 * Le drapeau `aggregated_from` est posé par le backend sur le seul JSON
 * agrégé. Sur une période, ou sur un match complet qui vaut une période unique,
 * il est absent : la ligne n'est alors pas marquée non agrégeable, mais une
 * valeur manquante y vaut désormais null et non plus zéro.
 */
function pctPairOrNA(a, b, isAggregate) {
  const missing = (v) => v === undefined || v === null
  if (isAggregate && missing(a) && missing(b)) {
    return [NOT_AGGREGATABLE, NOT_AGGREGATABLE]
  }
  return [numOrNull(a), numOrNull(b)]
}

/**
 * Un côté d'une paire pctPairOrNA. Volontairement construit à partir de la
 * paire plutôt que par une garde propre : `terrain` et les lignes `possession`
 * doivent porter EXACTEMENT la même valeur, NOT_AGGREGATABLE compris. Deux
 * gardes séparées finiraient par diverger.
 */
function sideOrNA(a, b, isAggregate, side) {
  const pair = pctPairOrNA(a, b, isAggregate)
  return side === 1 ? pair[0] : pair[1]
}

/**
 * Tiers dans LE SENS DU JEU de l'équipe considérée : son but à gauche, le but
 * adverse à droite. Cet ordre est celui du dessin, il ne doit pas être trié
 * ailleurs. Voir le bloc SÉMANTIQUE DES ZONES plus bas.
 */
const TIERS = [
  { key: 'defensive', label: 'Tiers défensif' },
  { key: 'milieu', label: 'Tiers médian' },
  { key: 'offensive', label: 'Tiers offensif' },
]

/**
 * Couloirs d'origine des attaques.
 *
 * ORIENTATION NON PROUVÉE — ON NE PREND PAS POSITION. Les libellés sont
 * délibérément neutres : « Couloir 1 » et « Couloir 3 », pas « Aile gauche » et
 * « Aile droite ». Le terrain les dessine en trois bandes, sans flèche ni
 * marqueur de côté. Les vrais libellés reviendront quand l'orientation sera
 * établie, pas avant.
 *
 * POURQUOI LE TEST QUI A TRANCHÉ LES TIERS NE S'APPLIQUE PAS ICI — il reposait
 * sur un dénominateur PARTAGÉ. `zones_pct` distribue la possession d'un même
 * terrain entre les deux équipes : sous lecture absolue, `defensive.equipe_1`
 * et `defensive.equipe_2` décrivent le même tiers physique et doivent donc
 * sommer à 100. Le relevé (57, 93, 50) l'a écarté, et le corollaire
 * defensive.equipe_1 ~ offensive.equipe_2 a confirmé la lecture relative.
 *
 * `origine_attaques_pct` n'a pas de dénominateur partagé : c'est la répartition
 * des attaques DE CHAQUE ÉQUIPE, qui somme à 100 par équipe sous les DEUX
 * lectures. Il n'existe donc aucune contrainte arithmétique à confronter, et
 * gauche/droite se répondent symétriquement : les deux lectures produisent des
 * payloads rigoureusement indiscernables. Aucun volume de données ne tranchera.
 *
 * CE QU'IL FAUDRAIT POUR LA PROUVER — une vérité terrain extérieure au payload.
 * Trois voies · le prestataire documente la convention ; ou on relève sur une
 * vidéo d'un match au payload fortement déséquilibré (par ex. 60/20/20) le côté
 * réellement emprunté, et on le confronte à la clef ; ou le coach l'atteste sur
 * un match dont on sait le plan de jeu latéralisé. Une seule de ces trois
 * suffit, et aucune n'est un calcul.
 *
 * Le risque est asymétrique et c'est ce qui justifie la prudence : une ligne de
 * texte mal orientée se corrige, une flèche dessinée sur un terrain affirme au
 * coach qu'il a attaqué du côté où il sait ne pas avoir attaqué, et emporte la
 * confiance dans tout le reste du rapport.
 */
const COULOIRS = [
  { key: 'gauche', label: 'Couloir 1' },
  { key: 'axe', label: 'Axe' },
  { key: 'droite', label: 'Couloir 3' },
]

/**
 * Pivote les blocs zonés du point de vue d'UNE équipe.
 *
 * Le payload est indexé par zone puis par équipe (`defensive.equipe_1`) ; un
 * terrain se dessine par équipe puis par zone. Ce pivot est fait ici, une fois,
 * sous le commentaire qui établit la sémantique — et non dans le JSX, où il
 * serait refait à chaque rendu et relu par personne.
 *
 * Chaque valeur suit la convention du fichier : un nombre est une mesure, null
 * une absence, NOT_AGGREGATABLE une valeur que le match complet ne peut pas
 * produire. `possession` et `couloirs` portent le troisième cas sur l'onglet
 * agrégé — c'est ce qui permet au terrain d'afficher « disponible en 1re et 2e
 * période » sur les tiers concernés au lieu d'une surface vide.
 */
function terrainPourEquipe(side, { zones, orig, pz, trz, isAggregate }) {
  const suffix = `equipe_${side}`
  return {
    tiers: TIERS.map(({ key, label }) => {
      const ratio = parseRatio(pz[key]?.[suffix])
      return {
        key,
        label,
        possession: sideOrNA(zones[key]?.equipe_1, zones[key]?.equipe_2, isAggregate, side),
        passes: { ok: ratio.ok, total: ratio.total, pct: ratio.pct },
        recup: numOrNull(trz[key]?.[`recup_${suffix}`]),
        perdus: numOrNull(trz[key]?.[`perdus_${suffix}`]),
      }
    }),
    couloirs: COULOIRS.map(({ key, label }) => ({
      key,
      label,
      pct: sideOrNA(orig.equipe_1?.[key], orig.equipe_2?.[key], isAggregate, side),
    })),
  }
}

/**
 * Transforme un AnalyseResult en groupes de lignes pour l'UI.
 * Chaque row = [valEquipe1, valEquipe2, label, unit]
 *
 * Une valeur de row vaut un nombre (mesure), null (non mesure, affiche en
 * tiret) ou NOT_AGGREGATABLE (non calculable sur le match complet).
 */
export function analyseResultToStatGroups(result) {
  if (!result || typeof result !== 'object') return null

  // Un objet bien forme n'est pas forcement un AnalyseResult. Le backend a
  // longtemps depose sous la cle `full` une enveloppe {match_id, mt1, mt2},
  // dont aucun des blocs ci-dessous n'existait : chaque lecture retombait sur
  // {} puis sur 0, et l'onglet affichait des zeros au lieu de dire qu'il
  // n'avait rien. On refuse maintenant explicitement ce qu'on ne sait pas lire.
  if (!result.match_info && !result.possession && !result.passes) return null

  const mi = result.match_info || {}
  const poss = result.possession || {}
  const zones = poss.zones_pct || {}
  const orig = poss.origine_attaques_pct || {}
  const passes = result.passes || {}
  const dir = passes.direction_pct || {}
  const glob = passes.global || {}
  const pz = passes.par_zone || {}
  const tr = result.transitions || {}
  const trz = tr.par_zone || {}
  const carto = result.cartographies || {}
  // Posé par le backend uniquement sur l'agrégat des deux périodes.
  const isAggregate = Array.isArray(result.aggregated_from)

  const passPct1 = passAccuracy(glob.reussies_equipe_1, glob.total_equipe_1)
  const passPct2 = passAccuracy(glob.reussies_equipe_2, glob.total_equipe_2)

  // SÉMANTIQUE DES ZONES — RELATIVE À L'ÉQUIPE, pas au terrain (établi 04/08/2026)
  //
  // Vaut pour les trois blocs zonés lus ci-dessous : `possession.zones_pct`,
  // `passes.par_zone` et `transitions.par_zone`. « defensive » désigne la zone
  // défensive DE CHAQUE ÉQUIPE. `defensive.equipe_1` et `defensive.equipe_2`
  // sont donc deux moitiés OPPOSÉES du terrain, jamais le même endroit.
  //
  // MÉTHODE DE VÉRIFICATION (match a90f3e5e, deux mi-temps, payload réel) —
  // les deux lectures sont mutuellement exclusives et se départagent ainsi :
  //
  //   Test relatif · somme des 3 zones d'une même équipe attendue à 100
  //     MT1 : equipe_1 = 100, equipe_2 = 100      -> VÉRIFIÉ
  //     MT2 : equipe_1 = 100, equipe_2 = 100      -> VÉRIFIÉ
  //
  //   Test absolu · somme des 2 équipes sur une même zone attendue à 100
  //     relevé : 57, 93, 50                       -> ÉCARTÉ sans ambiguïté
  //
  //   Corollaire · defensive.equipe_1 doit valoir offensive.equipe_2
  //     defensive.equipe_1 (35, 28) ~ offensive.equipe_2 (30, 23)  -> COHÉRENT
  //
  // CE CONSTAT NE DÉPEND PAS DE L'IDENTITÉ DES ÉQUIPES. Il établit ce que
  // « defensive » désigne, pas qui est `equipe_1`. Cette seconde question reste
  // ouverte et n'est pas tranchable sur les données dont on dispose : sur
  // a90f3e5e, le résultat réel est 5-0 pour le club, les cinq buts en seconde
  // période ; le pipeline en détecte bien cinq mais les répartit 3/2, dont un
  // en MT1. Le compte est juste, l'attribution est fausse — on ne peut donc
  // ancrer aucune équipe sur les buts détectés. Tant que le prestataire n'a pas
  // répondu, aucun nom de club n'est posé sur `equipe_1` (voir l'en-tête de
  // MatchAnalyseReport).
  //
  // CE QUE ÇA CONDAMNE À L'AFFICHAGE — mettre `equipe_1` et `equipe_2` en
  // barres divergentes sur une même ligne « Zone défensive », ce que fait le
  // rapport écrit par le prestataire, met en regard deux moitiés opposées de la
  // pelouse. Les chiffres sont justes, la lecture qu'ils induisent ne l'est pas.
  // Un terrain dessiné doit se lire du point de vue d'une seule équipe à la
  // fois, sens du jeu de gauche à droite, chacune dans SES tiers.
  //
  // Le backend s'appuie sur la même conclusion pour sommer ces clefs sur
  // MT1 + MT2 (analysis_api.py, constante _ZONES) : la somme n'est exacte que
  // sous cette lecture, puisque les équipes changent de camp à la mi-temps.
  // Refaire les trois tests ci-dessus avant de conclure autrement.
  const zoneDef = parseRatio(pz.defensive?.equipe_1)
  const zoneDef2 = parseRatio(pz.defensive?.equipe_2)
  const zoneMil = parseRatio(pz.milieu?.equipe_1)
  const zoneMil2 = parseRatio(pz.milieu?.equipe_2)
  const zoneOff = parseRatio(pz.offensive?.equipe_1)
  const zoneOff2 = parseRatio(pz.offensive?.equipe_2)

  // null quand la clef est absente, [] quand la mesure a ete faite sans
  // trouver de tir. ShotsList tolere les deux (il teste `shots?.length`).
  const tirs1 = Array.isArray(carto.tirs_equipe_1) ? carto.tirs_equipe_1 : null
  const tirs2 = Array.isArray(carto.tirs_equipe_2) ? carto.tirs_equipe_2 : null
  const nbTirs1 = listLengthOrNull(carto.tirs_equipe_1)
  const nbTirs2 = listLengthOrNull(carto.tirs_equipe_2)

  const corners1 = numOrNull(mi.corners_equipe_1)
  const corners2 = numOrNull(mi.corners_equipe_2)

  return {
    // LE SCORE ET LES BUTS SONT DE LA SAISIE COACH, JAMAIS DE LA DETECTION.
    //
    // `score1`, `score2` et `buts` viennent du payload du prestataire et sont
    // exposes ici, mais AUCUN affichage ne doit s'en servir. Le score montre au
    // coach est celui qu'il a saisi (`match.score_home` / `score_away`,
    // STATS-002) ; les buts ne sont plus montres du tout.
    //
    // POURQUOI · le pipeline detecte le BON NOMBRE de buts mais les ATTRIBUE
    // MAL. Sur a90f3e5e, resultat reel 5-0 pour le club, les cinq buts en
    // seconde periode ; sortie du prestataire 3-2, dont un but place dans une
    // mi-temps ou il ne s'est rien passe. Le compte juste rend l'erreur
    // credible, et c'est ce qui la rend dangereuse.
    //
    // Tant que l'attribution n'est pas fiable, ces trois champs ne remontent
    // pas au coach. Voir le bloc « BUTS RETIRE TEMPORAIREMENT » dans
    // MatchAnalyseReport.jsx pour le critere de retablissement — le compte
    // total ne suffit pas, il est deja juste aujourd'hui.
    //
    // NE PAS CONFONDRE AVEC `match_events`, qui porte les buts SAISIS par le
    // coach. Ce sont deux sources distinctes qui ne se croisent nulle part —
    // verifie le 04/08/2026 : `match_info.buts` n'est lu que par ce mapper et
    // par l'agregateur backend, `MatchEvent` que par routes/matches.py et
    // routes/players.py. Elles ne doivent jamais etre fusionnees ni comparees.
    matchInfo: {
      score1: numOrNull(mi.score_equipe_1),
      score2: numOrNull(mi.score_equipe_2),
      buts: Array.isArray(mi.buts) ? mi.buts : [],
      corners1,
      corners2,
    },
    possession: [
      [...pctPairOrNA(poss.globale_equipe_1_pct, poss.globale_equipe_2_pct, isAggregate), 'Possession globale', '%'],
      [...pctPairOrNA(zones.defensive?.equipe_1, zones.defensive?.equipe_2, isAggregate), 'Tiers défensif', '%'],
      [...pctPairOrNA(zones.milieu?.equipe_1, zones.milieu?.equipe_2, isAggregate), 'Tiers médian', '%'],
      [...pctPairOrNA(zones.offensive?.equipe_1, zones.offensive?.equipe_2, isAggregate), 'Tiers offensif', '%'],
    ],
    passes: [
      [numOrNull(glob.reussies_equipe_1), numOrNull(glob.reussies_equipe_2), 'Passes réussies', ''],
      // MANQUEES ET TOTAL ETAIENT LUS PAR PERSONNE. Le payload les porte
      // depuis toujours ; seules `reussies` et la precision calculee
      // remontaient au coach. Une passe manquee est une information a part
      // entiere — c'est elle qui dit le volume de deperdition, que la
      // precision seule masque sur un faible nombre de passes.
      [numOrNull(glob.manquees_equipe_1), numOrNull(glob.manquees_equipe_2), 'Passes manquées', ''],
      [numOrNull(glob.total_equipe_1), numOrNull(glob.total_equipe_2), 'Passes tentées', ''],
      [passPct1, passPct2, 'Précision de passe', '%'],
      [...pctPair(dir.avant?.equipe_1, dir.avant?.equipe_2), "Passes vers l'avant", '%'],
      [...pctPair(dir.lateral?.equipe_1, dir.lateral?.equipe_2), 'Passes latérales', '%'],
      [...pctPair(dir.arriere?.equipe_1, dir.arriere?.equipe_2), "Passes vers l'arrière", '%'],
    ],
    // CINQ ELEMENTS, PAS QUATRE · les deux derniers portent le ratio brut
    // « 45/52 » tel que le prestataire l'envoie. `parseRatio` le decoupait
    // depuis toujours pour n'en garder que le pourcentage · le numerateur et
    // le denominateur etaient calcules puis jetes. Un « 92 % » sur 12 passes
    // et un « 92 % » sur 240 ne disent pas la meme chose, et seul le ratio
    // permet de les distinguer.
    //
    // Les composants qui ne lisent que [v1, v2, label, unit] ignorent les
    // elements supplementaires · rien a adapter chez eux.
    passesZones: [
      [zoneDef.pct, zoneDef2.pct, 'Zone défensive', '%', zoneDef, zoneDef2],
      [zoneMil.pct, zoneMil2.pct, 'Zone milieu', '%', zoneMil, zoneMil2],
      [zoneOff.pct, zoneOff2.pct, 'Zone offensive', '%', zoneOff, zoneOff2],
    ],
    attaques: [
      [...pctPairOrNA(orig.equipe_1?.gauche, orig.equipe_2?.gauche, isAggregate), 'Aile gauche', '%'],
      [...pctPairOrNA(orig.equipe_1?.axe, orig.equipe_2?.axe, isAggregate), 'Axe', '%'],
      [...pctPairOrNA(orig.equipe_1?.droite, orig.equipe_2?.droite, isAggregate), 'Aile droite', '%'],
    ],
    transitions: [
      [numOrNull(tr.ballons_recuperes?.equipe_1), numOrNull(tr.ballons_recuperes?.equipe_2), 'Ballons récupérés', ''],
      [numOrNull(tr.ballons_perdus?.equipe_1), numOrNull(tr.ballons_perdus?.equipe_2), 'Ballons perdus', ''],
      [numOrNull(tr.recuperations_rapides_5s?.equipe_1), numOrNull(tr.recuperations_rapides_5s?.equipe_2), 'Récupérations < 5 s', ''],
      [numOrNull(tr.transitions_rapides_reussies_6s?.equipe_1), numOrNull(tr.transitions_rapides_reussies_6s?.equipe_2), 'Transitions offensives < 6 s', ''],
      [numOrNull(tr.touches_surface_adverse?.equipe_1), numOrNull(tr.touches_surface_adverse?.equipe_2), 'Ballons surface adverse', ''],
    ],
    // [name, recup1, perdus1, recup2, perdus2] — chaque compteur peut valoir
    // null. ZoneTable substitue le tiret cellule par cellule.
    zonesTurnover: [
      [
        'Zone défensive',
        numOrNull(trz.defensive?.recup_equipe_1), numOrNull(trz.defensive?.perdus_equipe_1),
        numOrNull(trz.defensive?.recup_equipe_2), numOrNull(trz.defensive?.perdus_equipe_2),
      ],
      [
        'Zone milieu',
        numOrNull(trz.milieu?.recup_equipe_1), numOrNull(trz.milieu?.perdus_equipe_1),
        numOrNull(trz.milieu?.recup_equipe_2), numOrNull(trz.milieu?.perdus_equipe_2),
      ],
      [
        'Zone offensive',
        numOrNull(trz.offensive?.recup_equipe_1), numOrNull(trz.offensive?.perdus_equipe_1),
        numOrNull(trz.offensive?.recup_equipe_2), numOrNull(trz.offensive?.perdus_equipe_2),
      ],
    ],
    // La ligne « Tirs cadrés / buts » a ete RETIREE. Elle etait calculee en
    // filtrant les tirs dont le champ `resultat` contient « cadr » ou « but »,
    // une heuristique sur un vocabulaire que le prestataire ne nous a jamais
    // documente et qu'aucun payload reel observe n'a permis de qualifier. Deux
    // defauts se superposaient : si le vocabulaire ne correspond pas, la ligne
    // affiche 0 sur des tirs bien cadres ; et faute de mesure, elle affichait 0
    // la ou il aurait fallu un tiret. On la remettra quand un payload reel
    // portant des tirs aura permis de fixer le vocabulaire.
    //
    // LES DEUX LIGNES SONT MARQUEES `unavailable` · NI L'UNE NI L'AUTRE N'EST
    // VALIDEE. `nbTirs` vient de `cartographies`, absent de tout l'historique,
    // et sort donc au tiret ; `corners` sort un nombre que rien ne verifie —
    // 8-0 releve sur un match reel. Le second cas est le plus couteux : un
    // chiffre faux mais plausible ne se distingue pas d'une mesure, et il
    // decredibilise les blocs voisins qui, eux, sont justes.
    //
    // LE MARQUEUR EST SUR LA LIGNE, PAS SUR LE BLOC · le jour ou l'une des deux
    // metriques est qualifiee, on retire son marqueur sans toucher a l'autre.
    //
    // ON N'ARRETE PAS DE LIRE LES CHAMPS POUR AUTANT · `counts.tirs`,
    // `counts.corners` et `analyseResultToLegacyStats` continuent de les
    // exposer. Seul l'affichage du rapport se tait.
    tirs: [
      [nbTirs1, nbTirs2, 'Tirs tentés', '', { unavailable: true }],
      [corners1, corners2, 'Corners', '', { unavailable: true }],
    ],
    tirsDetail: { equipe1: tirs1, equipe2: tirs2 },
    // Vue par équipe des trois blocs zonés, pour le terrain. Mêmes données que
    // `possession`, `passesZones` et `zonesTurnover` ci-dessus, pivotées — pas
    // une seconde source. Les trois lignes d'origine restent en place tant que
    // le terrain ne les a pas remplacées ; elles partiront avec lui.
    terrain: {
      equipe1: terrainPourEquipe(1, { zones, orig, pz, trz, isAggregate }),
      equipe2: terrainPourEquipe(2, { zones, orig, pz, trz, isAggregate }),
    },
    // Compteurs exposes hors des lignes d'affichage. analyseResultToLegacyStats
    // les indexait par position dans `tirs` et `passes` ; le retrait de la
    // ligne « Tirs cadrés / buts » aurait alors decale corners a la place des
    // tirs cadres, en silence. On ne lit plus une ligne d'UI comme une donnee.
    counts: {
      tirs: [nbTirs1, nbTirs2],
      passesReussies: [numOrNull(glob.reussies_equipe_1), numOrNull(glob.reussies_equipe_2)],
      passAccuracy: [passPct1, passPct2],
      possession: [
        ...pctPairOrNA(poss.globale_equipe_1_pct, poss.globale_equipe_2_pct, isAggregate),
      ],
      corners: [corners1, corners2],
    },
    cartographies: {
      heatmap1: carto.heatmap_description_equipe_1 || '',
      heatmap2: carto.heatmap_description_equipe_2 || '',
      turnover1: carto.turnover_description_equipe_1 || '',
      turnover2: carto.turnover_description_equipe_2 || '',
    },
  }
}

/**
 * Mappe AnalyseResult → ancien format match.stats (compat MatchDetail / Dashboard).
 *
 * `match.stats` est un contrat ancien, sans representation du « non mesure » :
 * MatchDetail interpole la valeur dans une chaine (`${v.home}${suffix}`) et
 * DashboardHome la pousse dans une sparkline. Un null y afficherait
 * « null — null », et le Symbol NOT_AGGREGATABLE y leverait une TypeError a
 * l'interpolation — ce dernier cas etait atteignable des qu'un match analyse en
 * deux periodes voyait sa possession omise par l'agregat, ce que le backend
 * fait systematiquement depuis P0-STATS-001.
 *
 * On n'emet donc une clef QUE si les deux cotes sont des nombres. Une clef
 * absente est deja geree par les deux consommateurs : MatchDetail filtre la
 * ligne, DashboardHome retombe sur null.
 *
 * `shots_on_target` n'est plus emis : la ligne « Tirs cadrés / buts » dont il
 * derivait a ete retiree du rapport, faute de vocabulaire qualifie cote
 * prestataire. MatchDetail masque la ligne « Tirs cadrés » de lui-meme.
 */
export function analyseResultToLegacyStats(result) {
  if (!result) return null
  const g = analyseResultToStatGroups(result)
  if (!g) return null

  const measured = ([v1, v2]) =>
    (typeof v1 === 'number' && typeof v2 === 'number') ? { home: v1, away: v2 } : undefined

  const stats = {
    possession: measured(g.counts.possession),
    passes: measured(g.counts.passesReussies),
    pass_accuracy: measured(g.counts.passAccuracy),
    shots: measured(g.counts.tirs),
    corners: measured(g.counts.corners),
  }

  const kept = Object.entries(stats).filter(([, v]) => v !== undefined)
  // Aucune stat mesuree : on rend null plutot qu'un objet vide. MatchDetail
  // teste `!match.stats` pour decider d'enrichir ; un {} truthy y bloquerait
  // definitivement l'enrichissement d'un rechargement ulterieur.
  if (!kept.length) return null
  return Object.fromEntries(kept)
}
